import { useEffect, useState } from "react";
import axios from "axios";
import { PRODUCTS as RAW_PRODUCTS } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// In-memory cache so the network calls only happen once per session
let overridesCache = null;
let customCache = null;
let inflight = null;

async function fetchData() {
  if (overridesCache && customCache) return { overrides: overridesCache, custom: customCache };
  if (!inflight) {
    inflight = Promise.all([
      axios.get(`${API}/products/overrides`).then(r => r.data || {}).catch(() => ({})),
      axios.get(`${API}/products/custom`).then(r => r.data || []).catch(() => []),
    ]).then(([o, c]) => {
      overridesCache = o;
      customCache = c;
      return { overrides: o, custom: c };
    });
  }
  return inflight;
}

function applyOverrides(rawList, overrides) {
  return rawList
    .map((p) => {
      const o = overrides[p.id];
      if (!o) return p;
      return {
        ...p,
        price: typeof o.price === "number" ? o.price : p.price,
        description: o.description ?? p.description,
        longDescription: o.long_description ?? p.longDescription,
        photo: o.photo ?? p.photo,
        gallery: Array.isArray(o.gallery) ? o.gallery : (p.gallery || []),
        _hidden: Boolean(o.hidden),
      };
    })
    .filter((p) => !p._hidden);
}

function normalizeCustom(c) {
  // Make custom DB shape compatible with static products.js shape
  return {
    id: c.id,
    name: c.name,
    category: c.category,
    extraCategories: c.extra_categories || [],
    price: c.price,
    strength: c.strength || "",
    volume: c.volume || "",
    description: c.description || "",
    longDescription: c.long_description || "",
    tags: c.tags || [],
    photo: c.photo || "",
    _custom: true,
  };
}

export function useProducts() {
  const [products, setProducts] = useState(() => {
    if (overridesCache && customCache) {
      const merged = [...RAW_PRODUCTS, ...customCache.map(normalizeCustom)];
      return applyOverrides(merged, overridesCache);
    }
    return RAW_PRODUCTS;
  });

  useEffect(() => {
    let cancelled = false;
    fetchData().then(({ overrides, custom }) => {
      if (cancelled) return;
      const merged = [...RAW_PRODUCTS, ...custom.map(normalizeCustom)];
      setProducts(applyOverrides(merged, overrides));
    });
    return () => { cancelled = true; };
  }, []);

  return products;
}

export function invalidateProductsCache() {
  overridesCache = null;
  customCache = null;
  inflight = null;
}
